import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CalendarClock,
  Check,
  ChevronRight,
  Home,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import { siteConfig, services, builders, testimonials, faqs } from "../mock";
import { VrindaLogo } from "../components/VrindaLogo";
import { PropertyTypesSection } from "../components/PropertyTypesSection";
import { FeaturedProjectsSection } from "../components/FeaturedProjectsSection";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "../components/ui/sonner";

const LS_KEY = "vrinda_enquiries_v1";

const useLocalStorageList = () => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Keep in sync if the user has multiple tabs open
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LS_KEY) return;
      try {
        setItems(e.newValue ? JSON.parse(e.newValue) : []);
      } catch {
        setItems([]);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItem = (item) => {
    const next = [item, ...items].slice(0, 10);
    setItems(next);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  };

  const clear = () => {
    setItems([]);
    localStorage.removeItem(LS_KEY);
  };

  return { items, addItem, clear };
};

const Section = ({ id, eyebrow, title, desc, children, className = "" }) => {
  return (
    <section id={id} className={`scroll-mt-28 ${className}`.trim()}>
      <div className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          {eyebrow && (
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/55 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur-xl">
              <Sparkles className="h-3.5 w-3.5 text-[#0F766E]" />
              {eyebrow}
            </div>
          )}
          <h2 className="mt-4 text-pretty text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            {title}
          </h2>
          {desc && (
            <p className="mt-3 text-pretty text-[15px] leading-7 text-slate-600 sm:text-base">
              {desc}
            </p>
          )}
        </div>

        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
};

const NavLink = ({ href, label }) => {
  return (
    <a
      href={href}
      className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-900/5 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F766E]/30"
    >
      {label}
    </a>
  );
};

export default function LandingPage() {
  const { items, addItem, clear } = useLocalStorageList();

  const [service, setService] = useState("rent");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    budget: "",
    message: "",
  });

  const contactCards = useMemo(() => {
    const c = siteConfig.contacts;
    return [
      {
        icon: Phone,
        title: "Call us",
        lines: [
          `${c.primaryContactName}: ${c.primaryPhone}`,
          `${c.secondaryContactName}: ${c.secondaryPhone}`,
        ],
        action: {
          label: "Call now",
          href: `tel:${c.primaryPhone}`,
        },
      },
      {
        icon: MapPin,
        title: "Visit office",
        lines: [c.addressLine1, c.addressLine2],
        action: {
          label: "Copy address",
          onClick: async () => {
            try {
              await navigator.clipboard.writeText(
                `${c.addressLine1}, ${c.addressLine2}`
              );
              toast.success("Address copied to clipboard");
            } catch {
              toast.message("Copy not available in this browser");
            }
          },
        },
      },
      {
        icon: Users,
        title: "Service area",
        lines: [c.serviceArea],
        action: {
          label: "WhatsApp",
          href: `https://wa.me/91${c.whatsapp}`,
          target: "_blank",
          rel: "noreferrer",
        },
      },
    ];
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();

    const phoneClean = (form.phone || "").replace(/\s+/g, "").trim();
    if (!form.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!/^\d{10}$/.test(phoneClean)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    const payload = {
      id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()),
      createdAt: new Date().toISOString(),
      service,
      ...form,
      phone: phoneClean,
    };

    addItem(payload);
    setForm({ name: "", phone: "", budget: "", message: "" });

    toast.success("Thanks! We’ll call you shortly.", {
      description: "Saved on this device (frontend-only mock).",
    });
  };

  const heroStats = [
    {
      icon: BadgeCheck,
      label: "Verified options",
      value: "Shortlisted" ,
    },
    {
      icon: CalendarClock,
      label: "Fast site visits",
      value: "Same-week" ,
    },
    {
      icon: ShieldCheck,
      label: "Paperwork support",
      value: "Guided" ,
    },
  ];

  return (
    <div className="min-h-screen bg-[#fbfaf7] text-slate-900">
      {/* Decorative layers */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_500px_at_15%_0%,rgba(15,118,110,0.12),transparent_55%),radial-gradient(900px_420px_at_90%_10%,rgba(212,167,44,0.12),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.05] [background-image:url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22 viewBox=%220 0 160 160%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22160%22 height=%22160%22 filter=%22url(%23n)%22 opacity=%220.6%22/%3E%3C/svg%3E')]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/55 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-6 lg:px-8">
          <a href="#top" className="group relative inline-flex items-center overflow-hidden rounded-2xl px-2 py-1">
            {/* subtle brand image layer behind logo */}
            <span className="pointer-events-none absolute inset-0">
              <img
                src={siteConfig.brand.heroImageUrl}
                alt=""
                className="h-full w-full object-cover opacity-[0.12]"
                loading="lazy"
              />
              <span className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/70 to-white/85" />
            </span>
            <span className="relative">
              <VrindaLogo />
            </span>
          </a>

          <nav className="hidden items-center gap-1 lg:flex">
            <NavLink href="#property" label="Property Types" />
            <NavLink href="#projects" label="Properties" />
            <NavLink href="#services" label="Services" />
            <NavLink href="#builders" label="Builders" />
            <NavLink href="#why" label="Why Vrinda" />
            <NavLink href="#faq" label="FAQ" />
            <NavLink href="#contact" label="Contact" />
          </nav>

          <div className="flex items-center gap-2">
            <a href={`tel:${siteConfig.contacts.primaryPhone}`} className="hidden sm:block">
              <Button
                variant="outline"
                className="border-slate-300 bg-white/50 backdrop-blur-xl transition-colors hover:bg-white"
              >
                <Phone className="mr-2 h-4 w-4" />
                Call
              </Button>
            </a>
            <a href="#contact">
              <Button className="bg-[#0B1220] text-white shadow-[0_10px_30px_rgba(11,18,32,0.24)] transition-colors hover:bg-[#121b31]">
                Enquire
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main id="top">
        <section className="relative overflow-hidden bg-[#0B1220]">
          <div className="absolute inset-0 -z-10">
            <img
              src={siteConfig.brand.heroImageUrl}
              alt="Modern residential buildings"
              className="h-full w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/60 to-[#fbfaf7]" />
            <div className="absolute inset-0 [background:radial-gradient(900px_420px_at_15%_25%,rgba(212,167,44,0.30),transparent_55%)]" />
          </div>

          <div className="mx-auto max-w-6xl px-5 pb-12 pt-14 sm:px-6 sm:pb-16 sm:pt-16 lg:px-8 lg:pb-24">
            <div className="grid items-end gap-10 lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/90 shadow-sm backdrop-blur-2xl">
                  <MapPin className="h-3.5 w-3.5 text-[#f4ede1]" />
                  {siteConfig.brand.city} Residential • Rent • Buy/Sell • Plots • Floors
                </div>

                <h1 className="mt-5 text-balance text-4xl font-semibold tracking-tight text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)] sm:text-5xl">
                  Premium real estate guidance—without the pressure.
                </h1>
                <p className="mt-4 max-w-xl text-pretty text-[15px] leading-7 text-white/90 drop-shadow-[0_1px_1px_rgba(0,0,0,0.32)] sm:text-base">
                  Vrinda Homes Real Estate helps you shortlist the right property, plan site visits,
                  and move forward with clarity—from rentals to independent floors.
                </p>

                <div className="relative mt-7 overflow-hidden rounded-2xl border border-white/15 bg-white/10 p-3 shadow-[0_20px_60px_rgba(0,0,0,0.22)] backdrop-blur-2xl">
                  {/* Dynamic photo accent behind CTAs */}
                  <div className="pointer-events-none absolute inset-0">
                    <img
                      src="https://d2dy9w7mmecm6m.cloudfront.net/dy-images/residential/GlimpsesOfMasterpiece/1_-_0Cj0ENTe8AMC.jpg"
                      alt="Project ambience"
                      className="h-full w-full object-cover opacity-[0.12]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220]/45 via-transparent to-[#0b1220]/45" />
                  </div>

                  <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center">
                    <a href="#contact">
                      <Button className="w-full bg-[#D4A72C] text-[#0B1220] shadow-[0_14px_40px_rgba(212,167,44,0.28)] transition-colors hover:bg-[#e0b63a] sm:w-auto">
                        Get a shortlist
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                    <a href="#services" className="sm:flex-1">
                      <Button
                        variant="outline"
                        className="w-full border-white/25 bg-white/10 text-white backdrop-blur-2xl transition-colors hover:bg-white/15"
                      >
                        Explore services
                      </Button>
                    </a>

                    <div className="hidden sm:block sm:flex-1" />
                    <div className="hidden sm:flex items-center justify-end gap-2 text-xs text-white/80">
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#D4A72C]" />
                        Site visits • Paperwork
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-10 grid gap-3 sm:grid-cols-3">
                  {heroStats.map((s) => (
                    <div
                      key={s.label}
                      className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 shadow-[0_18px_50px_rgba(0,0,0,0.22)] backdrop-blur-2xl"
                    >
                      <s.icon className="h-4 w-4 text-[#f4ede1]" />
                      <div className="mt-3 text-sm font-semibold text-white">
                        {s.value}
                      </div>
                      <div className="mt-1 text-xs text-white/80">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:pb-6">
                <Card className="relative overflow-hidden border-white/15 bg-white/10 shadow-[0_25px_70px_rgba(0,0,0,0.28)] backdrop-blur-2xl">
                  {/* Fill the empty top area with a subtle image strip */}
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-24">
                    <img
                      src="https://d2dy9w7mmecm6m.cloudfront.net/dy-images/residential/GlimpsesOfMasterpiece/3_-_0qFEDtsvOQ6s.jpg"
                      alt="Property ambience"
                      className="h-full w-full object-cover opacity-[0.16]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0b1220]/55 via-[#0b1220]/25 to-transparent" />
                    <div className="absolute inset-0 [background:radial-gradient(420px_160px_at_20%_40%,rgba(212,167,44,0.22),transparent_60%)]" />
                  </div>

                  <CardHeader className="relative pt-8">
                    <CardTitle className="text-white">Quick enquiry</CardTitle>
                    <p className="text-sm text-white/80">
                      Share your requirement—save locally (frontend-only mock).
                    </p>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={onSubmit} className="space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Input
                          value={form.name}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, name: e.target.value }))
                          }
                          placeholder="Your name"
                          className="border-white/20 bg-white/10 text-white placeholder:text-white/60 focus-visible:ring-[#D4A72C]/40"
                        />
                        <Input
                          value={form.phone}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, phone: e.target.value }))
                          }
                          placeholder="Phone (10-digit)"
                          inputMode="numeric"
                          className="border-white/20 bg-white/10 text-white placeholder:text-white/60 focus-visible:ring-[#D4A72C]/40"
                        />
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <Select value={service} onValueChange={setService}>
                          <SelectTrigger className="border-white/20 bg-white/10 text-white focus:ring-[#D4A72C]/40">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="rent">Rent</SelectItem>
                            <SelectItem value="sell">Buy / Sell</SelectItem>
                            <SelectItem value="plots">Plots</SelectItem>
                            <SelectItem value="floors">Independent Floors</SelectItem>
                          </SelectContent>
                        </Select>

                        <Input
                          value={form.budget}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, budget: e.target.value }))
                          }
                          placeholder="Budget (optional)"
                          className="border-white/20 bg-white/10 text-white placeholder:text-white/60 focus-visible:ring-[#D4A72C]/40"
                        />
                      </div>

                      <Textarea
                        value={form.message}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, message: e.target.value }))
                        }
                        placeholder="Any preferred sector / size / timeline?"
                        className="min-h-[92px] border-white/20 bg-white/10 text-white placeholder:text-white/60 focus-visible:ring-[#D4A72C]/40"
                      />

                      <Button
                        type="submit"
                        className="w-full bg-[#D4A72C] text-[#0B1220] transition-colors hover:bg-[#e0b63a]"
                      >
                        Submit enquiry
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>

                      <div className="grid gap-2 rounded-xl border border-white/15 bg-white/10 p-3 text-xs text-white/80 backdrop-blur-2xl">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-white">Fast response</span>
                          <span className="text-white/70">Gurgaon • Sohna</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {["Verified options", "Same-week visits", "Paperwork guidance"].map((x) => (
                            <span
                              key={x}
                              className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-2 py-1"
                            >
                              {x}
                            </span>
                          ))}
                        </div>
                        <div className="text-white/75">
                          Or call: <span className="font-semibold">{siteConfig.contacts.primaryPhone}</span>
                        </div>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Property types mood board */}
        <PropertyTypesSection />

        {/* Featured projects */}
        <FeaturedProjectsSection />

        {/* Services */}
        <Section
          id="services"
          eyebrow="What we do"
          title="Residential real estate services in Gurgaon"
          desc="Focused support for rentals, purchase/sale, plots and independent floors—designed to save your time and reduce uncertainty."
        >
          <div className="grid gap-5 md:grid-cols-2">
            {services.map((s) => (
              <Card
                key={s.id}
                className="group relative overflow-hidden border-slate-200/70 bg-white/65 shadow-sm backdrop-blur-2xl transition-shadow hover:shadow-[0_20px_55px_rgba(15,23,42,0.10)]"
              >
                <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(15,118,110,0.18),transparent_65%)]" />
                  <div className="absolute -left-24 -bottom-28 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(212,167,44,0.14),transparent_65%)]" />
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-950">
                    <Home className="h-5 w-5 text-[#0F766E]" />
                    {s.title}
                  </CardTitle>
                  <p className="text-sm leading-6 text-slate-600">{s.desc}</p>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {s.highlights.map((h) => (
                      <Badge
                        key={h}
                        variant="secondary"
                        className="bg-slate-900/5 text-slate-700"
                      >
                        <Check className="mr-1.5 h-3.5 w-3.5 text-[#0F766E]" />
                        {h}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-slate-200/70 bg-white/60 p-6 shadow-sm backdrop-blur-2xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-950">
                  Need a tailored shortlist?
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  Tell us your sector + budget + timeline—we’ll share the best matches.
                </div>
              </div>
              <a href="#contact" className="shrink-0">
                <Button className="bg-[#0B1220] text-white transition-colors hover:bg-[#121b31]">
                  Request shortlist
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </Section>

        {/* Builders */}
        <Section
          id="builders"
          eyebrow="Ecosystem"
          title="Builders we frequently operate around"
          desc="We often help clients explore inventory and nearby pockets around these builders’ projects (availability varies by time)."
          className="pt-0"
        >
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {builders.map((b) => (
              <Card
                key={b.name}
                className="border-slate-200/70 bg-white/65 shadow-sm backdrop-blur-2xl transition-transform hover:-translate-y-0.5"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-[#0F766E]" />
                    {b.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-xs font-medium text-slate-500">Focus</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {b.focus.map((f) => (
                        <Badge
                          key={f}
                          className="bg-[#0F766E]/10 text-[#0F766E] hover:bg-[#0F766E]/15"
                        >
                          {f}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-500">
                      Operating pockets
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {b.markets.map((m) => (
                        <Badge
                          key={m}
                          variant="secondary"
                          className="bg-slate-900/5 text-slate-700"
                        >
                          {m}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>

        {/* Why Vrinda */}
        <Section
          id="why"
          eyebrow="Trust & process"
          title="Why clients choose Vrinda Homes"
          desc="A simple process that keeps you informed—no confusion, no unnecessary back-and-forth."
          className="pt-0"
        >
          <Tabs defaultValue="clarity" className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-white/70 backdrop-blur-2xl">
              <TabsTrigger value="clarity">Clarity</TabsTrigger>
              <TabsTrigger value="speed">Speed</TabsTrigger>
              <TabsTrigger value="support">Support</TabsTrigger>
            </TabsList>

            <TabsContent value="clarity" className="mt-6">
              <div className="grid gap-5 lg:grid-cols-3">
                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-[#0F766E]" />
                      Verified options first
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    We begin with a shortlist that matches your budget, location and lifestyle—so you don’t waste weekends.
                  </CardContent>
                </Card>

                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BadgeCheck className="h-5 w-5 text-[#D4A72C]" />
                      Clear market guidance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    We explain pricing patterns and comparable options so you can make confident decisions without overpaying.
                  </CardContent>
                </Card>

                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-[#0B1220]" />
                      Right fit, not random
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    We prioritize what matters—parking, sunlight, society rules, maintenance—before you finalize.
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="speed" className="mt-6">
              <div className="grid gap-5 lg:grid-cols-3">
                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarClock className="h-5 w-5 text-[#0F766E]" />
                      Site visits, organised
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    We schedule back-to-back visits so you compare options quickly—especially helpful for rentals.
                  </CardContent>
                </Card>

                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-[#D4A72C]" />
                      Faster coordination
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    We coordinate availability and entry for you—saving repeated calls and uncertainty.
                  </CardContent>
                </Card>

                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-[#0B1220]" />
                      Fewer steps
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    One requirement form, one shortlist, one visit plan—simple flow that keeps momentum.
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="support" className="mt-6">
              <div className="grid gap-5 lg:grid-cols-3">
                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-[#0F766E]" />
                      Documentation guidance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    We help you stay clear on basic documents and timelines to avoid last-minute surprises.
                  </CardContent>
                </Card>

                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-[#D4A72C]" />
                      Communication that’s consistent
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    You’ll always know what’s next—shortlist, visit schedule, negotiation or follow-up.
                  </CardContent>
                </Card>

                <Card className="border-slate-200/70 bg-white/65 backdrop-blur-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BadgeCheck className="h-5 w-5 text-[#0B1220]" />
                      After-visit support
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm leading-6 text-slate-600">
                    We help you compare options calmly and choose what’s right—based on your priorities.
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {testimonials.map((t) => (
              <Card
                key={t.name}
                className="relative overflow-hidden border-slate-200/70 bg-white/65 shadow-sm backdrop-blur-2xl"
              >
                <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(15,118,110,0.14),transparent_65%)]" />
                <CardHeader>
                  <CardTitle className="text-base text-slate-950">{t.name}</CardTitle>
                  <div className="text-xs text-slate-600">{t.title}</div>
                </CardHeader>
                <CardContent className="text-sm leading-6 text-slate-700">
                  “{t.quote}”
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>

        {/* FAQ */}
        <Section
          id="faq"
          eyebrow="Answers"
          title="Frequently asked questions"
          desc="Quick clarity on coverage, visits, and how we work."
          className="pt-0"
        >
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr]">
            <Card className="border-slate-200/70 bg-white/65 shadow-sm backdrop-blur-2xl">
              <CardContent className="p-6">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((f, idx) => (
                    <AccordionItem
                      key={f.q}
                      value={`item-${idx}`}
                      className="border-slate-200/70"
                    >
                      <AccordionTrigger className="text-left text-slate-900 hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-6 text-slate-600">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            <div className="space-y-5">
              <Card className="border-slate-200/70 bg-white/65 shadow-sm backdrop-blur-2xl">
                <CardHeader>
                  <CardTitle className="text-base">What you’ll get</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-slate-700">
                  {["A curated shortlist", "A site visit plan", "Guidance on next steps"].map(
                    (x) => (
                      <div key={x} className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-lg bg-[#0F766E]/10 text-[#0F766E]">
                          <Check className="h-4 w-4" />
                        </span>
                        <div className="leading-6">{x}</div>
                      </div>
                    )
                  )}
                </CardContent>
              </Card>

              <Card className="border-slate-200/70 bg-[#0B1220] text-white shadow-[0_25px_70px_rgba(11,18,32,0.20)]">
                <CardContent className="p-6">
                  <div className="text-sm font-semibold">Prefer a quick call?</div>
                  <div className="mt-2 text-sm text-white/80">
                    We’ll understand your requirement and share options.
                  </div>
                  <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <a href={`tel:${siteConfig.contacts.primaryPhone}`} className="flex-1">
                      <Button className="w-full bg-[#D4A72C] text-[#0B1220] hover:bg-[#e0b63a]">
                        <Phone className="mr-2 h-4 w-4" />
                        Call now
                      </Button>
                    </a>
                    <a href="#contact" className="flex-1">
                      <Button
                        variant="outline"
                        className="w-full border-white/20 bg-white/10 text-white backdrop-blur-xl hover:bg-white/15"
                      >
                        Enquire
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </Section>

        {/* Contact */}
        <Section
          id="contact"
          eyebrow="Let’s connect"
          title="Contact Vrinda Homes Real Estate"
          desc="Share your requirement and we’ll respond. (Currently saves enquiries in your browser as a MOCK; backend can be added next.)"
          className="pt-0"
        >
          <div className="grid gap-8 lg:grid-cols-[1fr_0.95fr]">
            <Card className="relative overflow-hidden border-slate-200/70 bg-white/70 shadow-sm backdrop-blur-2xl">
              {/* Add a subtle real-estate themed band to remove empty whitespace */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-24">
                <img
                  src={siteConfig.brand.heroImageUrl}
                  alt="Residential backdrop"
                  className="h-full w-full object-cover opacity-[0.12]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-white/0 via-white/35 to-white/85" />
                <div className="absolute inset-0 [background:radial-gradient(420px_160px_at_20%_40%,rgba(15,118,110,0.18),transparent_60%)]" />
              </div>

              <CardHeader className="relative pt-8">
                <CardTitle>Enquiry form</CardTitle>
                <p className="text-sm text-slate-600">
                  Phone number is required so we can contact you.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={onSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <div className="mb-2 text-xs font-medium text-slate-600">
                        Name
                      </div>
                      <Input
                        value={form.name}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, name: e.target.value }))
                        }
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <div className="mb-2 text-xs font-medium text-slate-600">
                        Phone
                      </div>
                      <Input
                        value={form.phone}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, phone: e.target.value }))
                        }
                        placeholder="10-digit mobile"
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <div className="mb-2 text-xs font-medium text-slate-600">
                        Service
                      </div>
                      <Select value={service} onValueChange={setService}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rent">Rent</SelectItem>
                          <SelectItem value="sell">Buy / Sell</SelectItem>
                          <SelectItem value="plots">Plots</SelectItem>
                          <SelectItem value="floors">Independent Floors</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <div className="mb-2 text-xs font-medium text-slate-600">
                        Budget (optional)
                      </div>
                      <Input
                        value={form.budget}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, budget: e.target.value }))
                        }
                        placeholder="e.g., 25–35k rent / 1.2–1.6Cr"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-xs font-medium text-slate-600">
                      Requirement
                    </div>
                    <Textarea
                      value={form.message}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, message: e.target.value }))
                      }
                      placeholder="Preferred sector, size, possession timeline, etc."
                      className="min-h-[110px]"
                    />
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Button className="bg-[#0B1220] text-white hover:bg-[#121b31]">
                      Submit enquiry
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <div className="text-xs text-slate-500">
                      Office: {siteConfig.contacts.addressLine1}, {" "}
                      {siteConfig.contacts.addressLine2}
                    </div>
                  </div>
                </form>

                <Separator className="my-6" />

                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-950">
                      Recent enquiries (this device)
                    </div>
                    <div className="mt-1 text-xs text-slate-600">
                      Showing last {items.length} saved items.
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-slate-300"
                    onClick={() => {
                      clear();
                      toast.message("Cleared saved enquiries");
                    }}
                    disabled={!items.length}
                  >
                    Clear
                  </Button>
                </div>

                <div className="mt-4 rounded-xl border border-slate-200 bg-white/60">
                  <ScrollArea className="h-[220px]">
                    <div className="divide-y divide-slate-200">
                      {items.length ? (
                        items.map((it) => (
                          <div key={it.id} className="p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-slate-900">
                                {it.name}
                              </div>
                              <Badge className="bg-[#0F766E]/10 text-[#0F766E] hover:bg-[#0F766E]/15">
                                {it.service}
                              </Badge>
                            </div>
                            <div className="mt-1 text-xs text-slate-600">
                              {it.phone} • {new Date(it.createdAt).toLocaleString()}
                            </div>
                            {it.message ? (
                              <div className="mt-2 text-sm text-slate-700">
                                {it.message}
                              </div>
                            ) : null}
                          </div>
                        ))
                      ) : (
                        <div className="p-6 text-sm text-slate-600">
                          No enquiries yet. Fill the form above to create one.
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-5">
              <div className="grid gap-5">
                {contactCards.map((c) => (
                  <Card
                    key={c.title}
                    className="border-slate-200/70 bg-white/70 shadow-sm backdrop-blur-2xl"
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <c.icon className="h-5 w-5 text-[#0F766E]" />
                        {c.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-1 text-sm text-slate-700">
                        {c.lines.map((l) => (
                          <div key={l}>{l}</div>
                        ))}
                      </div>
                      {c.action?.href ? (
                        <a
                          href={c.action.href}
                          target={c.action.target}
                          rel={c.action.rel}
                        >
                          <Button
                            variant="outline"
                            className="border-slate-300 bg-white/60 backdrop-blur-2xl hover:bg-white"
                          >
                            {c.action.label}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </a>
                      ) : (
                        <Button
                          variant="outline"
                          className="border-slate-300 bg-white/60 backdrop-blur-2xl hover:bg-white"
                          onClick={c.action.onClick}
                          type="button"
                        >
                          {c.action.label}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border-slate-200/70 bg-white/70 shadow-sm backdrop-blur-2xl">
                <CardHeader>
                  <CardTitle className="text-base">Quick note</CardTitle>
                </CardHeader>
                <CardContent className="text-sm leading-6 text-slate-700">
                  We operate primarily in {siteConfig.brand.city} for residential rent, buy/sell, plots, and independent floors.
                  Share your budget and preferred sector to get a sharper shortlist.
                </CardContent>
              </Card>
            </div>
          </div>
        </Section>

        {/* Footer */}
        <footer className="relative overflow-hidden border-t border-slate-200/70 bg-white/45 backdrop-blur-2xl">
          {/* Footer backdrop image under the logo area */}
          <div className="pointer-events-none absolute inset-0">
            <img
              src={siteConfig.brand.heroImageUrl}
              alt="City skyline"
              className="h-full w-full object-cover opacity-[0.10]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/80 to-white/92" />
          </div>

          <div className="relative mx-auto max-w-6xl px-5 py-10 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
              <div className="relative flex items-start gap-4">
                <div className="relative">
                  <VrindaLogo markOnly />
                  <div className="pointer-events-none absolute -inset-6 -z-10 rounded-2xl bg-[radial-gradient(240px_120px_at_40%_40%,rgba(15,118,110,0.14),transparent_60%)]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-950">
                    Vrinda Homes Real Estate
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    {siteConfig.contacts.addressLine1}, {siteConfig.contacts.addressLine2}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {["Rent", "Buy/Sell", "Plots", "Independent Floors"].map((x) => (
                      <Badge
                        key={x}
                        variant="secondary"
                        className="bg-slate-900/5 text-slate-700"
                      >
                        {x}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <a href="#services">
                  <Button variant="ghost" className="text-slate-700 hover:bg-slate-900/5">
                    Services
                  </Button>
                </a>
                <a href="#builders">
                  <Button variant="ghost" className="text-slate-700 hover:bg-slate-900/5">
                    Builders
                  </Button>
                </a>
                <a href="#contact">
                  <Button className="bg-[#0B1220] text-white hover:bg-[#121b31]">
                    Contact
                  </Button>
                </a>
              </div>
            </div>

            <Separator className="my-8" />

            <div className="flex flex-col gap-2 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
              <div>
                © {new Date().getFullYear()} Vrinda Homes Real Estate. All rights reserved.
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#0F766E]" />
                  Gurgaon
                </span>
                <span className="text-slate-300">|</span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4A72C]" />
                  Sohna
                </span>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
